import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import amdGreeting from "@examples/amd-greeting.json";
import billReminder from "@examples/bill-reminder.json";
import inboundRestored from "@examples/inbound-restored.json";
import outageNotify from "@examples/outage-notify.json";
import { canRedo, canUndo, initHistory, pushHistory, redoHistory, undoHistory } from "./documentHistory";
import { loadDraft, saveDraft } from "./draftStorage";
import { addEdge, nextEdgeId, removeEdge } from "./edgeGraph";
import { FlowCanvas } from "./FlowCanvas";
import { Inspector } from "./Inspector";
import { layoutFlow } from "./layout";
import { addNode, nextNodeId, removeNode } from "./nodeGraph";
import { commitNodeDrag } from "./nodePosition";
import { ADDABLE_NODE_TEMPLATES } from "./nodeTemplates";
import { Palette } from "./Palette";
import { SaveTemplatePanel } from "./SaveTemplatePanel";
import { templateRequestFromSearch } from "./templateRoute";
import { chooseSimulationOutcome, startSimulation, type SimulationState } from "./simulate";
import { Simulator } from "./Simulator";
import type { FlowDocument, FlowNodePosition } from "./types";

// The shipped, schema-valid samples (docs/plans/06, plus the AMD greeting
// the designer opens on) — read directly via the @examples alias rather
// than copied, so this designer can never drift from what schema-check and
// the Go executor actually run.
const EXAMPLES: { name: string; label: string; document: FlowDocument }[] = [
  { name: "amd-greeting", label: "Live or voicemail greeting", document: amdGreeting as FlowDocument },
  { name: "outage-notify", label: "Outage notification", document: outageNotify as FlowDocument },
  { name: "bill-reminder", label: "Bill reminder", document: billReminder as FlowDocument },
  { name: "inbound-restored", label: "Inbound: service restored", document: inboundRestored as FlowDocument },
];
const FIRST_EXAMPLE = EXAMPLES[0]!;

/**
 * Picks one shipped example flow, renders it, and lets a selected node or
 * edge be edited.
 *
 * Edits are local-only React state — nothing is sent to a backend, and
 * switching the example dropdown discards any in-progress edit and
 * reloads the canonical fixture (including its own undo history — a fresh
 * document starts with a fresh, empty history, not a carried-over one).
 * Add/remove nodes (palette + Inspector's own Delete button) and
 * connect/edit/remove edges now exist. "Save as template"
 * (SaveTemplatePanel.tsx) publishes the current document as an immutable
 * flow version.
 *
 * `document` is a linear undo/redo history's own `present` value
 * (documentHistory.ts), not a plain `useState`, so every one of this
 * component's own edits — drag, add/remove, connect/disconnect, an
 * Inspector Apply — is one undo step. Selection is deliberately cleared on
 * undo/redo rather than preserved: the previously-selected node or edge may
 * not exist in the restored document (an undo can bring back a just-deleted
 * node, or make a just-added one disappear), and Inspector's own
 * `key={selectedNode.id}` remount is what resets a typed form's in-progress
 * draft to match whatever the canvas now shows — skipping that reset would
 * leave an editor open on stale, no-longer-current data.
 *
 * `simulation` (Simulator.tsx, simulate.ts) is a structural "test call"
 * walkthrough of the current document's own edges — not a real execution
 * proof — kept in this component alongside `document`/`history`/selection
 * for the same reason: any document edit, undo/redo or example switch ends
 * an in-progress test call explicitly, since simulate.ts's own state is a
 * snapshot of whatever graph existed when the call started and would
 * silently describe a document that no longer exists otherwise. The
 * simulation's current node (while still running) is passed to FlowCanvas
 * as `activeNodeId`, a highlight independent of node/edge selection —
 * running a test call does not select anything for editing.
 *
 * The document (and which example it started from) is autosaved to
 * `localStorage` on every change (draftStorage.ts) — one slot, not one per
 * example, so a reload restores whatever was most recently being worked
 * on. The undo history itself is NOT part of that snapshot: a restored
 * session starts a fresh history from the restored document, exactly as
 * switching examples already does, rather than trying to serialize
 * `documentHistory.ts`'s own past/future arrays too.
 */
export function FlowPreview({ canPlaceTestCall }: { canPlaceTestCall: boolean }) {
  const [selectedName, setSelectedName] = useState(() => loadDraft(window.localStorage)?.exampleName ?? FIRST_EXAMPLE.name);
  const [history, setHistory] = useState(() =>
    initHistory(loadDraft(window.localStorage)?.document ?? FIRST_EXAMPLE.document),
  );
  // Only worth announcing when the restored document actually diverges from
  // the canonical example it started from — a Reset (or a reload of a
  // never-edited session) also goes through this same saved slot, and
  // "restored your draft" would be a misleading thing to say about content
  // that is byte-identical to the shipped fixture.
  const [restoredDraft, setRestoredDraft] = useState(() => {
    const saved = loadDraft(window.localStorage);
    if (!saved) {
      return false;
    }
    const canonical = EXAMPLES.find((example) => example.name === saved.exampleName)?.document;
    return JSON.stringify(saved.document) !== JSON.stringify(canonical);
  });
  // Opened from the Templates list rather than as a fresh draft: the URL
  // carries which template, which version, and whether it is editable
  // (templateRoute.ts). Read once -- a later navigation remounts this.
  const [templateRequest] = useState(() => templateRequestFromSearch(window.location.search));
  const [template, setTemplate] = useState<{
    flowId: string;
    name: string;
    versionNumber: number;
    nextVersion: number;
  } | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [simulation, setSimulation] = useState<SimulationState | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const readOnly = templateRequest?.readOnly ?? false;
  const document = history.present;
  const positions = useMemo(() => layoutFlow(document), [document]);

  // Load the requested template once, replacing whatever draft was in
  // localStorage. An EDIT always takes the current version, never an older
  // one someone happened to be viewing, so the next version cannot be
  // built on a document that was already superseded.
  useEffect(() => {
    if (!templateRequest) return;
    let cancelled = false;
    void (async () => {
      try {
        const { versions } = await api.listTemplateVersions(templateRequest.flowId);
        const latest = versions[0];
        if (!latest) throw new Error("this template has no versions");
        const wanted = templateRequest.versionId
          ? versions.find((v) => v.flow_version_id === templateRequest.versionId)
          : latest;
        if (!wanted) throw new Error("that version is not part of this template");
        const published = await api.getFlowVersion(wanted.flow_version_id);
        if (cancelled) return;
        setHistory(initHistory(published.program as FlowDocument));
        setRestoredDraft(false);
        setTemplate({
          flowId: templateRequest.flowId,
          name: wanted.name,
          versionNumber: wanted.version_number,
          nextVersion: latest.version_number + 1,
        });
      } catch (err) {
        if (!cancelled) {
          setTemplateError(err instanceof Error ? err.message : "could not open that template");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateRequest]);

  // Every document mutation is a deliberate, discrete edit already (drag-
  // stop, add/remove, connect, an Inspector Apply — never a raw keystroke),
  // so this needs no debouncing of its own; re-saving the just-restored
  // (or still-canonical) document on mount is a harmless no-op write.
  useEffect(() => {
    // A template opened from the Templates list is not this browser's
    // draft, and writing it into that slot would silently replace work in
    // progress the next time the designer is opened normally.
    if (templateRequest) return;
    saveDraft(window.localStorage, { exampleName: selectedName, document });
  }, [selectedName, document, templateRequest]);

  // Every existing call site already passes either a document or a
  // `(current) => next` updater (the same shape plain `useState`'s own
  // setter accepts) — this wraps that in a history push instead of a plain
  // replace, so undo/redo needed no changes anywhere else in this file.
  // Any edit also ends an in-progress test call: simulate.ts's own state
  // (which node it is on, what edges it already walked) is a snapshot of
  // the document at the moment the call started, and would silently
  // describe a graph that no longer exists.
  function setDocument(next: FlowDocument | ((current: FlowDocument) => FlowDocument)) {
    setHistory((current) => {
      const resolved = typeof next === "function" ? next(current.present) : next;
      return pushHistory(current, resolved);
    });
    setSimulation(null);
  }

  const undo = useCallback(() => {
    setHistory(undoHistory);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setSimulation(null);
  }, []);

  const redo = useCallback(() => {
    setHistory(redoHistory);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setSimulation(null);
  }, []);

  // A global shortcut, not a `<textarea>`-scoped one: skipped entirely
  // while focus is inside an input/textarea/select, so the browser's own
  // native undo inside a field being actively typed into is never fought
  // over — this only ever undoes/redoes a whole applied document edit
  // (an Apply click, a drag, a connect), never in-progress keystrokes.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const activeTag = window.document.activeElement?.tagName.toLowerCase();
      if (activeTag === "input" || activeTag === "textarea" || activeTag === "select") {
        return;
      }
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "z") {
        return;
      }
      event.preventDefault();
      if (event.shiftKey) {
        redo();
      } else {
        undo();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undo, redo]);

  function selectExample(name: string) {
    const example = EXAMPLES.find((candidate) => candidate.name === name) ?? FIRST_EXAMPLE;
    setSelectedName(example.name);
    setHistory(initHistory(example.document));
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setSimulation(null);
    setRestoredDraft(false);
    setSavingTemplate(false);
  }

  function startTestCall() {
    setSimulation(startSimulation(document));
  }

  function chooseTestCallOutcome(outcome: string) {
    setSimulation((current) => (current ? chooseSimulationOutcome(document, current, outcome) : current));
  }

  function stopTestCall() {
    setSimulation(null);
  }

  function handleSelectNode(nodeId: string | null) {
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(null);
  }

  function handleSelectEdge(edgeId: string | null) {
    setSelectedEdgeId(edgeId);
    setSelectedNodeId(null);
  }

  function handleNodeDragStop(nodeId: string, newPosition: FlowNodePosition) {
    setDocument((current) => commitNodeDrag(current, positions, nodeId, newPosition));
  }

  function handleAddNode(type: string, config: unknown, position?: FlowNodePosition) {
    const id = nextNodeId(document, type);
    setDocument((current) => addNode(current, positions, id, type, config, position));
    handleSelectNode(id);
  }

  function handleDropNode(type: string, position: FlowNodePosition) {
    const template = ADDABLE_NODE_TEMPLATES.find((candidate) => candidate.type === type);
    if (template) handleAddNode(type, template.defaultConfig(), position);
  }

  function handleRemoveNode(nodeId: string) {
    setDocument((current) => removeNode(current, nodeId));
    handleSelectNode(null);
  }

  // Connections come from a specific port handle (FlowCanvas.tsx), so the
  // new edge already has the right port. A port takes one edge: wiring an
  // already-wired port moves it rather than adding a duplicate that
  // validation would then reject.
  function handleConnect(source: string, port: string, target: string) {
    const id = nextEdgeId(document);
    setDocument((current) => {
      const existing = current.edges.find((edge) => edge.source === source && edge.port === port);
      return addEdge(existing ? removeEdge(current, existing.id) : current, id, source, port, target);
    });
    handleSelectEdge(id);
  }

  function handleRemoveEdge(edgeId: string) {
    setDocument((current) => removeEdge(current, edgeId));
    handleSelectEdge(null);
  }

  return (
    <section aria-labelledby="flow-preview-heading" className="card">
      <div className="card__header">
        <div>
          <h2 id="flow-preview-heading">Flow designer</h2>
          <p className="card__subtitle">
            Start from a sample, edit it on the canvas, simulate it, then save it as a template
            and place a real test call.
          </p>
        </div>
        <span className="badge badge--preview">Preview</span>
      </div>
      <p className="card__note">
        Drag from an output on the right of a node (for example <strong>Live person</strong> or{" "}
        <strong>Key 1</strong>) to another node&rsquo;s input on its left to connect them. Click or drag
        a step from the palette to add it, and click a node or connection to edit it. Edits
        autosave to this browser.
      </p>
      {templateError ? (
        <p className="card__error" role="alert">
          {templateError}
        </p>
      ) : null}
      {template ? (
        <p className="card__note" role="status">
          {readOnly ? (
            <>
              Viewing <strong>{template.name}</strong> v{template.versionNumber} — a published
              version never changes, so this canvas is read-only. Open the template for editing
              to publish v{template.nextVersion}.
            </>
          ) : (
            <>
              Editing <strong>{template.name}</strong>, currently v{template.versionNumber}. Saving
              publishes v{template.nextVersion}; v{template.versionNumber} is kept, and anything
              already pointing at it keeps running it.
            </>
          )}
        </p>
      ) : null}
      {restoredDraft ? (
        <p className="card__note">
          Restored your unsaved draft of{" "}
          <strong>{EXAMPLES.find((example) => example.name === selectedName)?.label ?? selectedName}</strong>.{" "}
          <button type="button" className="btn--outline btn--sm" onClick={() => setRestoredDraft(false)}>
            Dismiss
          </button>
        </p>
      ) : null}
      <div className="flow-toolbar">
        {readOnly ? null : (
        <label className="flow-toolbar__sample">
          <span>Sample</span>
          <select value={selectedName} onChange={(event) => selectExample(event.target.value)}>
            {EXAMPLES.map((example) => (
              <option key={example.name} value={example.name}>
                {example.label}
              </option>
            ))}
          </select>
        </label>
        )}
        {readOnly ? null : (
        <button
          type="button"
          className="btn--outline"
          onClick={() => selectExample(selectedName)}
          title="Discard changes and reload the sample"
        >
          Reset
        </button>
        )}
        <button type="button" className="btn--outline" onClick={undo} disabled={!canUndo(history)} title="Undo (Ctrl/Cmd+Z)">
          Undo
        </button>
        <button
          type="button"
          className="btn--outline"
          onClick={redo}
          disabled={!canRedo(history)}
          title="Redo (Shift+Ctrl/Cmd+Z)"
        >
          Redo
        </button>
        <span className="flow-toolbar__spacer" />
        {readOnly ? (
          <span className="badge">Read-only</span>
        ) : (
          <button type="button" onClick={() => setSavingTemplate(true)} disabled={savingTemplate}>
            {template ? `Save as v${template.nextVersion}` : "Save as template"}
          </button>
        )}
      </div>
      {savingTemplate ? (
        <SaveTemplatePanel
          document={document}
          canPlaceTestCall={canPlaceTestCall}
          {...(template ? { template } : {})}
          onClose={() => setSavingTemplate(false)}
        />
      ) : null}
      <div className="flow-designer">
        {readOnly ? null : <Palette onAddNode={handleAddNode} />}
        <FlowCanvas
          document={document}
          positions={positions}
          selectedNodeId={selectedNodeId}
          selectedEdgeId={selectedEdgeId}
          activeNodeId={simulation && !simulation.ended ? simulation.currentNodeId : null}
          onSelectNode={handleSelectNode}
          onSelectEdge={handleSelectEdge}
          onNodeDragStop={handleNodeDragStop}
          onConnect={handleConnect}
          onDropNode={handleDropNode}
          readOnly={readOnly}
        />
        {/* Only while something is selected, so the canvas gets the full
            width the rest of the time. */}
        {!readOnly && (selectedNodeId || selectedEdgeId) ? (
          <Inspector
            document={document}
            selectedNodeId={selectedNodeId}
            selectedEdgeId={selectedEdgeId}
            onChange={setDocument}
            onRemoveNode={handleRemoveNode}
            onRemoveEdge={handleRemoveEdge}
          />
        ) : null}
      </div>
      <Simulator
        simulation={simulation}
        onStart={startTestCall}
        onChoose={chooseTestCallOutcome}
        onStop={stopTestCall}
      />
    </section>
  );
}
